import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../db/firebase";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import {
  HiOutlineCalendarDays,
  HiOutlineMusicalNote,
  HiOutlineDocumentText,
  HiOutlineTv,
} from "react-icons/hi2";
import ServiceEventDetail from "../Organization/Components/ServiceEventDetail";
import PresentationMode from "../Organization/Events/Components/PresentationMode";
import { SongArtwork } from "../../components/SongArtwork";

interface User {
  id: string;
  firstName: string;
  lastName: string;
}

interface Song {
  id: string;
  title: string;
  artist: string;
  album: string;
  artworkURL: string;
}

interface Comment {
  id: string;
  userName: string;
  comment: string;
  timestamp: string;
  attachmentUrl?: string;
  attachmentType?: string;
  attachmentName?: string;
  isAudio?: boolean;
}

const UserEventDetail: React.FC = () => {
  const { t } = useTranslation();
  const { organizationId } = useAuth();
  const { eventId } = useParams<{ eventId: string }>();
  const [event, setEvent] = useState<any | null>(null);
  const [leaders, setLeaders] = useState<User[]>([]);
  const [members, setMembers] = useState<User[]>([]);
  const [songs, setSongs] = useState<Song[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showPresentationMode, setShowPresentationMode] = useState(false);

  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        if (eventId && organizationId) {
          const eventDocRef = doc(db, "events", eventId);
          const eventDoc = await getDoc(eventDocRef);
          if (
            eventDoc.exists() &&
            eventDoc.data().organizationId === organizationId
          ) {
            const eventData = eventDoc.data();
            setEvent(eventData);

            // Fetch leaders and members details
            const leaderPromises = (eventData.leaders || []).map(
              async (leaderId: string) => {
                const userDoc = await getDoc(doc(db, "users", leaderId));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  return {
                    id: userDoc.id,
                    firstName: userData.firstName,
                    lastName: userData.lastName,
                  };
                }
                return null;
              },
            );

            const memberPromises = (eventData.members || []).map(
              async (memberId: string) => {
                const userDoc = await getDoc(doc(db, "users", memberId));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  return {
                    id: userDoc.id,
                    firstName: userData.firstName,
                    lastName: userData.lastName,
                  };
                }
                return null;
              },
            );

            const leaderResults = await Promise.all(leaderPromises);
            const memberResults = await Promise.all(memberPromises);

            setLeaders(leaderResults.filter((user) => user !== null) as User[]);
            setMembers(memberResults.filter((user) => user !== null) as User[]);

            // Fetch songs details
            const songPromises = (eventData.songs || []).map(
              async (songId: string) => {
                const songDoc = await getDoc(doc(db, "songs", songId));
                if (songDoc.exists()) {
                  const songData = songDoc.data();
                  return {
                    id: songDoc.id,
                    title: songData.title,
                    artist: songData.artist,
                    album: songData.album,
                    artworkURL: songData.artworkURL,
                  };
                }
                return null;
              },
            );

            const songResults = await Promise.all(songPromises);
            setSongs(songResults.filter((song) => song !== null) as Song[]);

            // Fetch and set comments
            setComments(eventData.comments || []);
          } else {
            setError(t("eventDoesNotExist"));
          }
        } else {
          setError(t("invalidEventId"));
        }
      } catch (error) {
        console.error(t("errorFetchingEventDetails"), error);
        setError(t("failedToFetchEventDetails"));
      }
    };

    fetchEventDetails();
  }, [eventId, organizationId, t]);

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`;
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f4ff] dark:bg-gray-900 p-4 sm:p-6 lg:p-8">
        <div className="rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 w-full max-w-md text-center">
          <p className="text-red-500 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f4ff] dark:bg-gray-900 p-4 sm:p-6 lg:p-8">
        <div className="rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 w-full max-w-md text-center">
          <p className="text-gray-700 dark:text-gray-300">{t("loading")}</p>
        </div>
      </div>
    );
  }

  // Render ServiceEventDetail if the event type is "Service"
  return event.type === "Service" ? (
    <ServiceEventDetail
      event={event}
      eventId={eventId!}
      organizationId={event.organizationId!}
    />
  ) : (
    <div className="min-h-screen bg-[#f7f4ff] dark:bg-gray-900 p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        {/* Header */}
        <div className="rounded-2xl bg-white dark:bg-gray-800 shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-8 sm:px-8 sm:py-10">
            <div className="flex items-start gap-5">
              <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-[#5932EA]/10 dark:bg-[#5932EA]/20 flex items-center justify-center text-[#5932EA]">
                <HiOutlineCalendarDays className="w-7 h-7" strokeWidth={2} />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
                  {event.title}
                </h1>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium">
                    {event.date
                      ? new Date(`${event.date}T00:00:00`).toLocaleDateString(
                          undefined,
                          {
                            weekday: "short",
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          },
                        )
                      : "—"}
                  </span>
                  <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium">
                    {event.time || "N/A"}
                  </span>
                </div>
                {event.description && (
                  <p className="text-gray-600 dark:text-gray-300 mt-4 leading-relaxed">
                    {event.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Details and Songs - side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Details card */}
          <div className="rounded-2xl bg-white dark:bg-gray-800 shadow-lg shadow-gray-200/50 dark:shadow-none border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
              <div className="w-1 h-6 rounded-full bg-gradient-to-b from-[#5932EA] to-[#7C3AED]" />
              <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                {t("details")}
              </h2>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t("type")}
                </span>
                <p className="text-gray-800 dark:text-gray-200 font-medium mt-0.5">
                  {event.type}
                </p>
              </div>
              {leaders.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                    {t("voces")}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {leaders.map((leader) => (
                      <div
                        key={leader.id}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#5932EA]/5 dark:bg-[#5932EA]/10 border border-[#5932EA]/10 dark:border-[#5932EA]/20"
                        title={`${leader.firstName} ${leader.lastName}`}
                      >
                        <span className="w-6 h-6 rounded-full bg-[#5932EA] text-white flex items-center justify-center text-xs font-semibold shrink-0">
                          {getInitials(leader.firstName, leader.lastName)}
                        </span>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                          {leader.firstName} {leader.lastName}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {members.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                    {t("musicians")}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {members.map((member) => (
                      <div
                        key={member.id}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#10B981]/5 dark:bg-[#10B981]/10 border border-[#10B981]/20 dark:border-[#10B981]/30"
                        title={`${member.firstName} ${member.lastName}`}
                      >
                        <span className="w-6 h-6 rounded-full bg-[#10B981] text-white flex items-center justify-center text-xs font-semibold shrink-0">
                          {getInitials(member.firstName, member.lastName)}
                        </span>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                          {member.firstName} {member.lastName}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Songs card */}
          <div className="rounded-2xl bg-white dark:bg-gray-800 shadow-lg shadow-gray-200/50 dark:shadow-none border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="w-1 h-6 rounded-full bg-gradient-to-b from-[#5932EA] to-[#7C3AED]" />
                <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                  {t("songs")}{" "}
                  {songs.length > 0 && (
                    <span className="text-gray-500 dark:text-gray-400 font-normal">
                      ({songs.length})
                    </span>
                  )}
                </h2>
              </div>
              {songs.length > 0 && (
                <button
                  onClick={() => setShowPresentationMode(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium bg-[#5932EA] text-white hover:bg-[#4a28c7] transition-colors shadow-sm"
                >
                  <HiOutlineTv className="w-5 h-5" />
                  {t("presentationMode")}
                </button>
              )}
            </div>
            <div className="p-6">
              {songs.length > 0 ? (
                <div className="flex flex-col sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {songs.map((song) => (
                    <Link
                      key={song.id}
                      to={`/songs/${song.id}`}
                      className="group flex flex-row sm:flex-col items-center gap-4 sm:gap-0 rounded-xl border border-gray-100 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-700/30 overflow-hidden hover:border-[#5932EA]/40 hover:shadow-md hover:shadow-[#5932EA]/5 transition-all duration-200 p-3 sm:p-0"
                    >
                      <div className="w-16 h-16 sm:w-full sm:aspect-square shrink-0 bg-gray-200 dark:bg-gray-600 flex items-center justify-center overflow-hidden rounded-lg sm:rounded-none">
                        <SongArtwork
                          src={song.artworkURL}
                          alt={song.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          iconClassName="w-8 h-8 sm:w-14 sm:h-14"
                        />
                      </div>
                      <div className="flex-1 min-w-0 sm:p-4 text-left sm:text-center">
                        <h3 className="font-semibold text-gray-800 dark:text-gray-100 line-clamp-2 text-sm leading-snug">
                          {song.title}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 sm:mt-1 truncate">
                          {song.artist}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-4">
                    <HiOutlineMusicalNote
                      className="w-8 h-8 text-gray-400 dark:text-gray-500"
                      strokeWidth={1.5}
                    />
                  </div>
                  <p className="font-medium text-gray-600 dark:text-gray-400">
                    {t("noSongsYet")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Notes card */}
        <div className="rounded-2xl bg-white dark:bg-gray-800 shadow-lg shadow-gray-200/50 dark:shadow-none border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
            <div className="w-1 h-6 rounded-full bg-gradient-to-b from-[#5932EA] to-[#7C3AED]" />
            <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
              {t("notes")}
            </h2>
          </div>
          <div className="p-6">
            {comments.length > 0 ? (
              <div className="space-y-4">
                {comments.map((comment) => (
                  <div
                    key={comment.id}
                    className="rounded-xl p-4 bg-gray-50 dark:bg-gray-700/30 border border-gray-100 dark:border-gray-600"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-gray-800 dark:text-gray-200">
                        {comment.userName}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(comment.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-700 dark:text-gray-300">
                      {comment.comment}
                    </p>
                    {comment.attachmentUrl && (
                      <div className="mt-3">
                        {comment.attachmentType === "image" && (
                          <img
                            src={comment.attachmentUrl}
                            alt="Attachment"
                            className="max-w-full h-auto rounded-lg"
                          />
                        )}
                        {comment.attachmentType === "audio" && (
                          <audio controls className="w-full mt-2">
                            <source
                              src={comment.attachmentUrl}
                              type="audio/mpeg"
                            />
                            {t("browserNotSupportAudio")}
                          </audio>
                        )}
                        {comment.attachmentType !== "image" &&
                          comment.attachmentType !== "audio" && (
                            <a
                              href={comment.attachmentUrl}
                              download={comment.attachmentName}
                              className="text-[#5932EA] hover:text-[#4a28c7] text-sm font-medium"
                            >
                              {comment.attachmentName ||
                                t("downloadAttachment")}
                            </a>
                          )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
                  <HiOutlineDocumentText
                    className="w-6 h-6 text-gray-400 dark:text-gray-500"
                    strokeWidth={1.5}
                  />
                </div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t("noComments")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showPresentationMode && songs.length > 0 && (
        <PresentationMode
          songIds={songs.map((s) => s.id)}
          eventTitle={event.title}
          organizationId={organizationId}
          onClose={() => setShowPresentationMode(false)}
        />
      )}
    </div>
  );
};

export default UserEventDetail;
